import React, { useContext, useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Typography,
  Avatar,
  Tabs,
  Tab,
  Paper,
  Button,
  IconButton,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { styled, useTheme, useMediaQuery } from "@mui/system";
import { useParams, useNavigate } from "react-router-dom";
import UserContext from "../context/userContext";
import { database, firebase, functions, firestore, storage } from "../firebaseConfig";
import { ref as dbRef, onValue, off } from "firebase/database";
import { httpsCallable } from "firebase/functions";
import { COLORS } from "../utils/COLORS";
import PetsIcon from "@mui/icons-material/Pets";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import FavoriteIcon from "@mui/icons-material/Favorite";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { toast } from "react-simple-toasts";
import logoImage from "../assets/logo.png";

// STYLED COMPONENTS
const PageContainer = styled(Box)({
  width: "100vw",
  maxWidth: "100%",
  minHeight: "100vh",
  backgroundColor: "#000000",
  color: "#FFFFFF",
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
  position: "relative",
  overflow: "hidden",
});

const BackgroundGlow = styled(Box)({
  position: "absolute",
  top: "-50%",
  left: "-50%",
  width: "200%",
  height: "200%",
  background: "radial-gradient(ellipse at center, rgba(16, 185, 129, 0.03) 0%, transparent 70%)",
  animation: "float 20s ease-in-out infinite",
  "@keyframes float": {
    "0%, 100%": { transform: "translate(0px, 0px) scale(1)" },
    "33%": { transform: "translate(30px, -30px) scale(1.1)" },
    "66%": { transform: "translate(-20px, 20px) scale(0.9)" },
  },
});

const Header = styled(Box)(({ theme }) => ({
  position: "relative",
  zIndex: 10,
  padding: "40px 0 80px 0",
  background: "linear-gradient(180deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0.95) 100%)",
  [theme.breakpoints.down("md")]: {
    padding: "32px 0 60px 0",
  },
}));

const HeaderContent = styled(Box)(({ theme }) => ({
  maxWidth: "800px",
  margin: "0 auto",
  padding: "0 32px",
  [theme.breakpoints.down("md")]: {
    padding: "0 24px",
  },
}));

const TopNav = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "60px",
});

const Logo = styled(Box)({
  display: "flex",
  alignItems: "center",
  fontSize: "18px",
  fontWeight: "600",
  color: "#FFFFFF",
  gap: "8px",
  cursor: "pointer",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    color: "#10B981",
  },
});

const ProfileSection = styled(Box)(({ theme }) => ({
  textAlign: "center",
  position: "relative",
  [theme.breakpoints.down("md")]: {
    textAlign: "center",
  },
}));

const ProfileAvatar = styled(motion.div)(({ theme }) => ({
  width: "120px",
  height: "120px",
  borderRadius: "50%",
  background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "48px",
  fontWeight: "700",
  color: "#FFFFFF",
  margin: "0 auto 24px auto",
  position: "relative",
  cursor: "pointer",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: "-4px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #10B981, #059669, #10B981)",
    opacity: 0,
    transition: "opacity 0.3s ease",
    zIndex: -1,
  },
  "&:hover::before": {
    opacity: 0.5,
  },
  [theme.breakpoints.down("md")]: {
    width: "100px",
    height: "100px",
    fontSize: "40px",
  },
}));

const Username = styled(Typography)(({ theme }) => ({
  fontSize: "42px",
  fontWeight: "300",
  color: "#FFFFFF",
  marginBottom: "8px",
  letterSpacing: "-0.02em",
  background: "linear-gradient(135deg, #FFFFFF 0%, #9CA3AF 100%)",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  [theme.breakpoints.down("md")]: {
    fontSize: "32px",
  },
}));

const UserEmail = styled(Typography)({
  fontSize: "16px",
  color: "#6B7280",
  marginBottom: "40px",
  fontWeight: "400",
});

const StatsContainer = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: "40px",
  maxWidth: "500px",
  margin: "0 auto",
  [theme.breakpoints.down("md")]: {
    gap: "32px",
  },
}));

const StatCard = styled(motion.div)(({ theme }) => ({
  textAlign: "center",
  padding: "24px 16px",
  borderRadius: "16px",
  background: "rgba(15, 23, 42, 0.6)",
  border: "1px solid rgba(30, 41, 59, 0.5)",
  backdropFilter: "blur(10px)",
  cursor: "pointer",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    background: "rgba(15, 23, 42, 0.8)",
    borderColor: "rgba(16, 185, 129, 0.3)",
  },
  [theme.breakpoints.down("md")]: {
    padding: "20px 12px",
  },
}));

const StatValue = styled(Typography)({
  fontSize: "28px",
  fontWeight: "700",
  background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  marginBottom: "4px",
});

const StatLabel = styled(Typography)({
  fontSize: "13px",
  color: "#9CA3AF",
  fontWeight: "500",
  letterSpacing: "0.5px",
});

const MainContent = styled(Box)(({ theme }) => ({
  maxWidth: "800px",
  margin: "0 auto",
  padding: "0 32px 80px 32px",
  position: "relative",
  zIndex: 10,
  [theme.breakpoints.down("md")]: {
    padding: "0 24px 60px 24px",
  },
}));

const LogoutButton = styled(motion.div)({
  padding: "12px 24px",
  borderRadius: "12px",
  background: "rgba(30, 41, 59, 0.6)",
  border: "1px solid rgba(55, 65, 81, 0.5)",
  color: "#9CA3AF",
  fontSize: "14px",
  fontWeight: "500",
  cursor: "pointer",
  transition: "all 0.3s ease",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  "&:hover": {
    background: "rgba(30, 41, 59, 0.8)",
    color: "#FFFFFF",
    transform: "translateY(-2px)",
  },
});

const TabsContainer = styled(Box)({
  marginBottom: "48px",
  position: "relative",
});

const StyledTabs = styled(Tabs)({
  "& .MuiTabs-flexContainer": {
    borderRadius: "16px",
    background: "rgba(15, 23, 42, 0.6)",
    border: "1px solid rgba(30, 41, 59, 0.5)",
    backdropFilter: "blur(10px)",
    padding: "6px",
  },
  "& .MuiTabs-indicator": {
    display: "none",
  },
  "& .MuiTab-root": {
    color: "#9CA3AF",
    fontWeight: "500",
    fontSize: "14px",
    textTransform: "none",
    minWidth: "auto",
    padding: "16px 24px",
    borderRadius: "12px",
    margin: "0 4px",
    transition: "all 0.3s ease",
    position: "relative",
    zIndex: 1,
    "&.Mui-selected": {
      color: "#FFFFFF",
      background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
      boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
    },
    "&:hover:not(.Mui-selected)": {
      color: "#FFFFFF",
      background: "rgba(30, 41, 59, 0.8)",
    },
  },
});

const ContentContainer = styled(motion.div)({
  borderRadius: "24px",
  background: "rgba(15, 23, 42, 0.4)",
  border: "1px solid rgba(30, 41, 59, 0.3)",
  backdropFilter: "blur(20px)",
  overflow: "hidden",
});

const SectionHeader = styled(Box)({
  padding: "32px 32px 0 32px",
  borderBottom: "1px solid rgba(30, 41, 59, 0.3)",
  paddingBottom: "24px",
  marginBottom: "32px",
});

const SectionTitle = styled(Typography)({
  fontSize: "24px",
  fontWeight: "300",
  color: "#FFFFFF",
  display: "flex",
  alignItems: "center",
  gap: "12px",
  letterSpacing: "-0.01em",
});

const ItemsContainer = styled(Box)({
  padding: "0 32px 32px 32px",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});

const ItemCard = styled(motion.div)(({ theme }) => ({
  background: "rgba(31, 41, 55, 0.6)",
  borderRadius: "16px",
  padding: "24px",
  border: "1px solid rgba(55, 65, 81, 0.4)",
  transition: "all 0.3s ease",
  cursor: "pointer",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "-100%",
    width: "100%",
    height: "100%",
    background: "linear-gradient(90deg, transparent, rgba(16, 185, 129, 0.1), transparent)",
    transition: "left 0.5s ease",
  },
  "&:hover": {
    transform: "translateY(-2px)",
    borderColor: "rgba(16, 185, 129, 0.4)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
    "&::before": {
      left: "100%",
    },
  },
  [theme.breakpoints.down("md")]: {
    padding: "20px",
  },
}));

const ItemHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  marginBottom: "16px",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    gap: "12px",
  },
}));

const ItemInfo = styled(Box)({
  flex: 1,
});

const ItemName = styled(Typography)({
  fontSize: "18px",
  fontWeight: "600",
  color: "#FFFFFF",
  marginBottom: "6px",
  letterSpacing: "-0.01em",
});

const ItemAmount = styled(Typography)({
  fontSize: "16px",
  fontWeight: "600",
  background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
});

const ItemMeta = styled(Typography)({
  fontSize: "14px",
  color: "#6B7280",
  marginTop: "8px",
  fontWeight: "400",
});

const StatusChip = styled(motion.div)(({ status }) => {
  const colors = {
    active: { bg: "rgba(16, 185, 129, 0.2)", border: COLORS.primaryGreen, text: COLORS.primaryGreen },
    trialing: { bg: "rgba(245, 158, 11, 0.2)", border: COLORS.warning, text: COLORS.warning },
    pending: { bg: "rgba(241, 196, 15, 0.2)", border: COLORS.yellow, text: COLORS.yellow },
    processed: { bg: "rgba(46, 204, 113, 0.2)", border: COLORS.primaryGreen, text: COLORS.primaryGreen },
    succeeded: { bg: "rgba(16, 185, 129, 0.2)", border: COLORS.primaryGreen, text: COLORS.primaryGreen },
  };

  const colorScheme = colors[status] || colors.pending;

  return {
    padding: "6px 12px",
    borderRadius: "8px",
    background: colorScheme.bg,
    border: `1px solid ${colorScheme.border}`,
    color: colorScheme.text,
    fontSize: "12px",
    fontWeight: "600",
    textTransform: "capitalize",
  };
});

const ActionButton = styled(motion.div)(({ variant = "danger" }) => ({
  padding: "8px 16px",
  borderRadius: "8px",
  fontSize: "13px",
  fontWeight: "500",
  cursor: "pointer",
  transition: "all 0.3s ease",
  display: "flex",
  alignItems: "center",
  gap: "6px",
  border: variant === "danger" ? "1px solid rgba(239, 68, 68, 0.4)" : "1px solid rgba(55, 65, 81, 0.4)",
  background: variant === "danger" ? "rgba(239, 68, 68, 0.1)" : "rgba(55, 65, 81, 0.1)",
  color: variant === "danger" ? "#EF4444" : "#9CA3AF",
  "&:hover": {
    background: variant === "danger" ? "rgba(239, 68, 68, 0.2)" : "rgba(55, 65, 81, 0.2)",
    transform: "translateY(-1px)",
  },
}));

const EmptyState = styled(Box)(({ theme }) => ({
  textAlign: "center",
  padding: "80px 32px",
  [theme.breakpoints.down("md")]: {
    padding: "60px 24px",
  },
}));

const EmptyIcon = styled(motion.div)({
  fontSize: "64px",
  color: "#374151",
  marginBottom: "24px",
  display: "flex",
  justifyContent: "center",
});

const EmptyTitle = styled(Typography)({
  fontSize: "20px",
  fontWeight: "300",
  color: "#9CA3AF",
  marginBottom: "8px",
  letterSpacing: "-0.01em",
});

const EmptyDescription = styled(Typography)({
  fontSize: "14px",
  color: "#6B7280",
  lineHeight: "1.5",
});

const StyledDialog = styled(Dialog)({
  "& .MuiDialog-paper": {
    backgroundColor: "#0F172A",
    border: "1px solid #1E293B",
    borderRadius: "16px",
    color: "#FFFFFF",
    maxWidth: "400px",
  },
});

const DialogTitleStyled = styled(DialogTitle)({
  fontSize: "20px",
  fontWeight: "600",
  color: "#FFFFFF",
  padding: "24px 24px 16px 24px",
});

const DialogContentStyled = styled(DialogContent)({
  padding: "0 24px 24px 24px",
  color: "#9CA3AF",
  fontSize: "14px",
  lineHeight: "1.5",
});

const DialogActionsStyled = styled(DialogActions)({
  padding: "16px 24px 24px 24px",
  gap: "12px",
});

const ConfirmButton = styled(Button)({
  backgroundColor: "#EF4444",
  color: "#FFFFFF",
  borderRadius: "8px",
  padding: "10px 20px",
  fontSize: "14px",
  fontWeight: "500",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#DC2626",
  },
});

const CancelDialogButton = styled(Button)({
  backgroundColor: "transparent",
  color: "#9CA3AF",
  border: "1px solid #374151",
  borderRadius: "8px",
  padding: "10px 20px",
  fontSize: "14px",
  fontWeight: "500",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#1F2937",
    borderColor: "#4B5563",
    color: "#FFFFFF",
  },
});

const ProfilePage = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user, setUser } = useContext(UserContext);
  const [activeTab, setActiveTab] = useState(0);
  const [subscriptions, setSubscriptions] = useState([]);
  const [pledges, setPledges] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [cancelingSubscription, setCancelingSubscription] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [subscriptionToCancel, setSubscriptionToCancel] = useState(null);
  const [showSignOutDialog, setShowSignOutDialog] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Redirect if user is not logged in or accessing wrong profile
  useEffect(() => {
    if (!user || user.username !== username) {
      navigate("/");
    }
  }, [user, username, navigate]);

  // Fetch user data
  useEffect(() => {
    if (!user) return;

    // Fetch subscriptions from Firestore
    const subscriptionsRef = firestore.collection("users").doc(user.uid).collection("subscriptions");
    const subscriptionsUnsubscribe = subscriptionsRef.onSnapshot((snapshot) => {
      if (!snapshot.empty) {
        const subscriptionsArray = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(
          subscription => subscription.status !== "canceled"
        );
        setSubscriptions(subscriptionsArray);
      } else {
        setSubscriptions([]);
      }
    });

    // Fetch pledges from Firestore - query flat pledges collection
    const fetchPledges = async () => {
      try {
        const pledgesSnapshot = await firestore
          .collection("pledges")
          .where("userId", "==", user.uid)
          .get();

        const allPledges = pledgesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setPledges(allPledges);
      } catch (error) {
        console.error("Error fetching pledges:", error);
        setPledges([]);
      }
      setIsLoading(false);
    };
    
    fetchPledges();

    return () => {
      subscriptionsUnsubscribe();
      // pledges are fetched once, no need to unsubscribe
    };
  }, [user]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleLogout = async () => {
    try {
      await firebase.auth().signOut();
      navigate("/");
    } catch (error) {
      toast("Error signing out");
    }
  };

  const handleSignOutClick = () => {
    setShowSignOutDialog(true);
  };

  const handleCancelSubscriptionClick = (subscription) => {
    setSubscriptionToCancel(subscription);
    setShowCancelDialog(true);
  };

  const handleConfirmCancelSubscription = async () => {
    if (!subscriptionToCancel || cancelingSubscription === subscriptionToCancel.subscriptionId) return;
    
    setCancelingSubscription(subscriptionToCancel.subscriptionId);
    setShowCancelDialog(false);
    
    try {
      const cancelSubscription = httpsCallable(functions, "cancelSubscription");
      const result = await cancelSubscription({ subscriptionId: subscriptionToCancel.subscriptionId });
      
      if (result.data.success) {
        toast("Subscription canceled successfully!");
      } else {
        throw new Error("Failed to cancel subscription");
      }
    } catch (error) {
      console.error("Error canceling subscription:", error);
      toast("Failed to cancel subscription: " + error.message);
    } finally {
      setCancelingSubscription(null);
      setSubscriptionToCancel(null);
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      toast('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast('Image must be less than 5MB');
      return;
    }

    setIsUploadingPhoto(true);

    try {
      // Create storage reference
      const fileRef = storage.ref(`avatars/${user.uid}.jpg`);

      // Upload file with metadata
      await fileRef.put(file, {
        contentType: file.type,
        cacheControl: 'public, max-age=31536000',
      });

      // Get public URL
      const photoURL = await fileRef.getDownloadURL();

      // Update Firestore
      await firestore.collection('users').doc(user.uid).update({
        photoURL: photoURL,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp()
      });

      // Update UserContext to show new photo immediately
      setUser({
        ...user,
        photoURL: photoURL
      });

      toast('Profile photo updated!');
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast('Failed to upload photo: ' + error.message);
    } finally {
      setIsUploadingPhoto(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (!user) {
    return null;
  }

  const totalPledgeAmount = pledges.reduce((sum, pledge) => sum + (pledge.displayAmount || 0), 0);
  const totalSubscriptionAmount = subscriptions.reduce((sum, sub) => sum + (sub.amount || 0), 0);

  return (
    <PageContainer>
      <BackgroundGlow />
      
      <Header>
        <HeaderContent>
          <TopNav>
            <Logo onClick={() => navigate("/")}>
              <img
                src={logoImage}
                alt="PledgeFast"
                style={{ width: "32px", height: "auto" }}
              />
              <Typography sx={{ fontSize: "24px", fontWeight: "600", color: "#FFFFFF" }}>
                PledgeFast
              </Typography>
            </Logo>
            <LogoutButton
              onClick={handleSignOutClick}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <LogoutIcon sx={{ fontSize: "18px" }} />
              Sign Out
            </LogoutButton>
          </TopNav>
          
          <ProfileSection>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*"
              style={{ display: 'none' }}
            />
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
              {(() => {
                // Only use photoURL if it's from our Firebase Storage
                const isOurPhoto = user.photoURL?.includes('firebase') || user.photoURL?.includes('firebasestorage');
                const photoToShow = isOurPhoto ? user.photoURL : null;

                return (
                  <ProfileAvatar
                    whileHover={{ scale: 1.05, rotate: 5 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleAvatarClick}
                    style={{
                      backgroundImage: photoToShow ? `url(${photoToShow})` : undefined,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      opacity: isUploadingPhoto ? 0.5 : 1,
                    }}
                  >
                    {isUploadingPhoto ? (
                      <CircularProgress size={48} sx={{ color: '#FFFFFF' }} />
                    ) : (
                      !photoToShow && user.username?.charAt(0).toUpperCase()
                    )}
                  </ProfileAvatar>
                );
              })()}
              {!isUploadingPhoto && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '8px',
                    right: '8px',
                    backgroundColor: COLORS.primaryGreen,
                    borderRadius: '50%',
                    width: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    border: '3px solid #000000',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    }
                  }}
                  onClick={handleAvatarClick}
                >
                  <CameraAltIcon sx={{ color: '#FFFFFF', fontSize: '20px' }} />
                </Box>
              )}
            </Box>
            <Username>{user.username}</Username>
            <UserEmail>{user.email}</UserEmail>
            
            <StatsContainer>
              <StatCard
                whileHover={{ scale: 1.05, y: -4 }}
                whileTap={{ scale: 0.95 }}
              >
                <StatValue>${totalPledgeAmount}</StatValue>
                <StatLabel>Total Pledged</StatLabel>
              </StatCard>
              <StatCard
                whileHover={{ scale: 1.05, y: -4 }}
                whileTap={{ scale: 0.95 }}
              >
                <StatValue>{pledges.length}</StatValue>
                <StatLabel>Dogs Supported</StatLabel>
              </StatCard>
              <StatCard
                whileHover={{ scale: 1.05, y: -4 }}
                whileTap={{ scale: 0.95 }}
              >
                <StatValue>{subscriptions.length}</StatValue>
                <StatLabel>Subscriptions</StatLabel>
              </StatCard>
            </StatsContainer>
          </ProfileSection>
        </HeaderContent>
      </Header>

      <MainContent>
        <TabsContainer>
          <StyledTabs value={activeTab} onChange={handleTabChange}>
            <Tab
              icon={<FavoriteIcon />}
              iconPosition="start"
              label="Pledges"
            />
            <Tab
              icon={<SubscriptionsIcon />}
              iconPosition="start"
              label="Subscriptions"
            />
            <Tab
              icon={<SettingsIcon />}
              iconPosition="start"
              label="Settings"
            />
          </StyledTabs>
        </TabsContainer>

        <AnimatePresence mode="wait">
          <ContentContainer
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            {activeTab === 0 && (
              <>
                <SectionHeader>
                  <SectionTitle>
                    <FavoriteIcon sx={{ color: "#10B981" }} />
                    One-Time Pledges
                  </SectionTitle>
                </SectionHeader>

                {pledges.length === 0 ? (
                  <EmptyState>
                    <EmptyIcon
                      animate={{
                        scale: [1, 1.1, 1],
                        rotate: [0, 10, -10, 0]
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    >
                      <FavoriteIcon sx={{ fontSize: "inherit" }} />
                    </EmptyIcon>
                    <EmptyTitle>No pledges yet</EmptyTitle>
                    <EmptyDescription>
                      Make a pledge to help save a dog in need
                    </EmptyDescription>
                  </EmptyState>
                ) : (
                  <ItemsContainer>
                    {pledges.map((pledge, index) => (
                      <ItemCard
                        key={pledge.orderId}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ scale: 1.02 }}
                        onClick={() => navigate(`/${pledge.slug}`)}
                        style={{ cursor: "pointer" }}
                      >
                        <ItemHeader>
                          <ItemInfo>
                            <ItemName>{pledge.name}</ItemName>
                            <ItemAmount>${pledge.displayAmount}</ItemAmount>
                          </ItemInfo>
                          <StatusChip status={pledge.status === "requires_capture" ? "pending" : pledge.status}>
                            {pledge.status === "requires_capture" ? "Pending" :
                             pledge.status?.charAt(0).toUpperCase() + pledge.status?.slice(1)}
                          </StatusChip>
                        </ItemHeader>
                        <ItemMeta>
                          {pledge.breed} • {pledge.age} • Created: {new Date(pledge.createdAt).toLocaleDateString()}
                        </ItemMeta>
                      </ItemCard>
                    ))}
                  </ItemsContainer>
                )}
              </>
            )}

            {activeTab === 1 && (
              <>
                <SectionHeader>
                  <SectionTitle>
                    <SubscriptionsIcon sx={{ color: "#10B981" }} />
                    Monthly Subscriptions
                  </SectionTitle>
                </SectionHeader>

                {subscriptions.length === 0 ? (
                  <EmptyState>
                    <EmptyIcon
                      animate={{
                        y: [0, -10, 0],
                        rotate: [0, 5, -5, 0]
                      }}
                      transition={{
                        duration: 4,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    >
                      <SubscriptionsIcon sx={{ fontSize: "inherit" }} />
                    </EmptyIcon>
                    <EmptyTitle>No active subscriptions</EmptyTitle>
                    <EmptyDescription>
                      Subscribe to support dogs and get updates on their rescue journey
                    </EmptyDescription>
                  </EmptyState>
                ) : (
                  <ItemsContainer>
                    {subscriptions.map((subscription, index) => (
                      <ItemCard
                        key={subscription.subscriptionId}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ scale: 1.02 }}
                        onClick={() => navigate(`/${subscription.dogSlug}`)}
                        style={{ cursor: "pointer" }}
                      >
                        <ItemHeader>
                          <ItemInfo>
                            <ItemName>{subscription.dogName}</ItemName>
                            <ItemAmount>${subscription.amount}/month</ItemAmount>
                          </ItemInfo>
                          <Box sx={{ display: "flex", gap: "12px", alignItems: "center" }}>
                            <StatusChip status={subscription.status}>
                              {subscription.status?.charAt(0).toUpperCase() + subscription.status?.slice(1)}
                            </StatusChip>
                            <ActionButton
                              variant="danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCancelSubscriptionClick(subscription);
                              }}
                              whileHover={cancelingSubscription === subscription.subscriptionId ? {} : { scale: 1.05 }}
                              whileTap={cancelingSubscription === subscription.subscriptionId ? {} : { scale: 0.95 }}
                              style={{
                                opacity: cancelingSubscription === subscription.subscriptionId ? 0.7 : 1,
                                cursor: cancelingSubscription === subscription.subscriptionId ? "not-allowed" : "pointer",
                              }}
                            >
                              {cancelingSubscription === subscription.subscriptionId ? (
                                <>
                                  <Box
                                    sx={{
                                      width: "14px",
                                      height: "14px",
                                      border: "2px solid #EF4444",
                                      borderTop: "2px solid transparent",
                                      borderRadius: "50%",
                                      animation: "spin 1s linear infinite",
                                      "@keyframes spin": {
                                        "0%": { transform: "rotate(0deg)" },
                                        "100%": { transform: "rotate(360deg)" },
                                      },
                                    }}
                                  />
                                  Canceling...
                                </>
                              ) : (
                                <>
                                  <CancelIcon sx={{ fontSize: "16px" }} />
                                  Cancel
                                </>
                              )}
                            </ActionButton>
                          </Box>
                        </ItemHeader>
                        <ItemMeta>
                          Created: {new Date(subscription.createdAt).toLocaleDateString()}
                        </ItemMeta>
                      </ItemCard>
                    ))}
                  </ItemsContainer>
                )}
              </>
            )}

            {activeTab === 2 && (
              <>
                <SectionHeader>
                  <SectionTitle>
                    <SettingsIcon sx={{ color: "#10B981" }} />
                    Account Settings
                  </SectionTitle>
                </SectionHeader>
                
                <EmptyState>
                  <EmptyIcon
                    animate={{ 
                      rotate: [0, 360],
                    }}
                    transition={{ 
                      duration: 8,
                      repeat: Infinity,
                      ease: "linear"
                    }}
                  >
                    <SettingsIcon sx={{ fontSize: "inherit" }} />
                  </EmptyIcon>
                  <EmptyTitle>Settings coming soon</EmptyTitle>
                  <EmptyDescription>
                    Account management features will be available here
                  </EmptyDescription>
                </EmptyState>
              </>
            )}
          </ContentContainer>
        </AnimatePresence>
      </MainContent>

      {/* Confirmation Dialogs */}
      <StyledDialog
        open={showSignOutDialog}
        onClose={() => setShowSignOutDialog(false)}
      >
        <DialogTitleStyled>Sign Out</DialogTitleStyled>
        <DialogContentStyled>
          Are you sure you want to sign out of your account?
        </DialogContentStyled>
        <DialogActionsStyled>
          <CancelDialogButton onClick={() => setShowSignOutDialog(false)}>
            Cancel
          </CancelDialogButton>
          <ConfirmButton onClick={() => {
            setShowSignOutDialog(false);
            handleLogout();
          }}>
            Sign Out
          </ConfirmButton>
        </DialogActionsStyled>
      </StyledDialog>

      <StyledDialog
        open={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
      >
        <DialogTitleStyled>Cancel Subscription</DialogTitleStyled>
        <DialogContentStyled>
          Are you sure you want to cancel your subscription for{" "}
          <strong style={{ color: "#FFFFFF" }}>
            {subscriptionToCancel?.dogName}
          </strong>
          ? This action cannot be undone and you'll stop receiving updates about this dog's rescue progress.
        </DialogContentStyled>
        <DialogActionsStyled>
          <CancelDialogButton onClick={() => {
            setShowCancelDialog(false);
            setSubscriptionToCancel(null);
          }}>
            Keep Subscription
          </CancelDialogButton>
          <ConfirmButton onClick={handleConfirmCancelSubscription}>
            Cancel Subscription
          </ConfirmButton>
        </DialogActionsStyled>
      </StyledDialog>
    </PageContainer>
  );
};

export default ProfilePage;